/**
 * ModerationTakedowns - what is currently off the site, and how to put it back
 * -----------------------------------------------------------------------------
 * Taking a chore or an offer down went in on 17 August 2026 with no way to
 * reverse it: `hiddenAt` could be cleared, but no screen did it, so undoing a
 * mistake meant editing the database. In practice that means it does not get
 * undone, and the mistakes that matter are exactly the ones somebody would
 * want to fix quietly and quickly - the wrong one of two similar chores, a
 * description misread at eleven at night.
 *
 * So this is really two things, and the list is the more important one. A
 * restore button on its own would be unreachable: a hidden chore can only be
 * found through a report that happens to point at it, and offers are never
 * attached to reports at all. Something taken down by mistake would simply be
 * invisible to the person who did it.
 *
 * It deliberately shows the REASON that was given. A moderator deciding
 * whether to put something back is usually reading somebody else's decision,
 * or their own from a fortnight ago, and "what did we say at the time" is the
 * whole question.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { EyeOff, Undo2 } from 'lucide-react';

import { safetyApi } from '../services/api';
import { dateTime } from '../utils/format';
import Loader from './Loader';
import EmptyState from './EmptyState';
import { useI18n } from '../hooks/useI18n';

export default function ModerationTakedowns() {
  const { t } = useI18n();
  const [data, setData] = useState({ chores: [], offers: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await safetyApi.takedowns());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const restore = async (kind, id) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = kind === 'offer' ? await safetyApi.restoreOffer(id) : await safetyApi.restoreChore(id);
      setNotice(res.message);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loader label={t('moderation.loadingTakedowns')} />;

  const nothing = !data.chores.length && !data.offers.length;

  return (
    <>
      {notice && <div className="alert alert-success" style={{ marginBottom: 'var(--space-sm)' }}>{notice}</div>}
      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-sm)' }}>{error}</div>}

      {nothing ? (
        <EmptyState
          icon={EyeOff}
          title={t('moderation.nothingTakenDown')}
          message={t('moderation.takedownsEmptyBody')}
        />
      ) : (
        <>
          {data.chores.map((c) => (
            <div className="card" key={c._id} style={{ marginBottom: 'var(--space-sm)' }}>
              <h3 style={{ fontSize: 'var(--text-h4)' }}>{c.title}</h3>
              <div className="card-meta" style={{ marginTop: 4 }}>
                <span>{t('moderation.choreLabel')}</span>
                <span>
                  {t('moderation.postedBy')}{' '}
                  {c.postedBy ? (
                    <Link to={`/users/${c.postedBy._id}`}>
                      {c.postedBy.firstName} {c.postedBy.lastName}
                    </Link>
                  ) : (
                    'somebody who has since left'
                  )}
                </span>
                <span>{dateTime(c.hiddenAt)}</span>
              </div>

              <div
                className="card"
                style={{ background: 'var(--color-bg-subtle)', boxShadow: 'none', marginTop: 'var(--space-xs)' }}
              >
                <p className="text-sm">
                  <strong>{t('moderation.reasonGiven')}</strong> {c.hiddenReason}
                </p>
                {c.hiddenBy && (
                  <p className="text-sm text-muted" style={{ marginTop: 4 }}>
                    By {c.hiddenBy.firstName} {c.hiddenBy.lastName}
                  </p>
                )}
              </div>

              <div className="card-footer">
                <span className="text-sm text-muted">{t('moderation.offMarketplace')}</span>
                <button
                  type="button"
                  className="btn btn-outline btn-sm btn-inline"
                  onClick={() => restore('chore', c._id)}
                  disabled={busy}
                >
                  <Undo2 size={16} /> {t('moderation.putItBack')}
                </button>
              </div>
            </div>
          ))}

          {data.offers.map((o) => (
            <div className="card" key={o._id} style={{ marginBottom: 'var(--space-sm)' }}>
              <h3 style={{ fontSize: 'var(--text-h4)' }}>
                {o.student ? `${o.student.firstName} ${o.student.lastName}` : 'A student'}
              </h3>
              <div className="card-meta" style={{ marginTop: 4 }}>
                <span>{t('moderation.availability')}</span>
                <span>{dateTime(o.hiddenAt)}</span>
              </div>

              {o.description && (
                <p className="text-sm text-muted" style={{ marginTop: 'var(--space-xs)' }}>
                  {o.description}
                </p>
              )}

              <div
                className="card"
                style={{ background: 'var(--color-bg-subtle)', boxShadow: 'none', marginTop: 'var(--space-xs)' }}
              >
                <p className="text-sm">
                  <strong>{t('moderation.reasonGiven')}</strong> {o.hiddenReason}
                </p>
                {o.hiddenBy && (
                  <p className="text-sm text-muted" style={{ marginTop: 4 }}>
                    By {o.hiddenBy.firstName} {o.hiddenBy.lastName}
                  </p>
                )}
              </div>

              <div className="card-footer">
                <span className="text-sm text-muted">{t('moderation.offWhosFree')}</span>
                <button
                  type="button"
                  className="btn btn-outline btn-sm btn-inline"
                  // The OFFER's own id. A student can have three, and this
                  // list shows each on its own row - so restoring puts back
                  // exactly the one clicked, not everything they had.
                  onClick={() => restore('offer', o._id)}
                  disabled={busy}
                >
                  <Undo2 size={16} /> {t('moderation.putItBack')}
                </button>
              </div>
            </div>
          ))}
        </>
      )}
    </>
  );
}
